/**
 * Filter panel for the ASNTab.
 *
 * CR-6449: one shell for both views, matching the Deliveries tab — the header
 * (title, Refresh, Export, Filters toggle with active count) stays visible when
 * the panel is collapsed; only the sections and the Clear all / Apply filters
 * footer hide. The supplier view renders the design's four sections
 * (`ASNFilterSupplierLayout`); the customer view keeps its CR-6441 body
 * (`ASNFilterSections`). Only the section body differs between the two.
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { X, Search, Filter, RefreshCw, Download } from 'lucide-react';
import { ASNFilterSections } from './asn/ASNFilterSections';
import { ASNFilterSupplierLayout } from './asn/ASNFilterSupplierLayout';
import type { ASNFilterOption } from './asn/asnFilterOptions';

interface ASNFilterSectionProps {
  filters: {
    asnNumber: string;
    supplier: string;
    trackingNumber: string;
    poNumber: string;
    poLineNumber: string;
    partNumber: string;
    description: string;
    status: string;
    courier: string;
    courierDeliveryStatus: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    warehouse: string;
    supplierCountry: string;
    supplierGroup: string;
    commodityGroup: string;
    commodityType: string;
    strategic: string;
    // CR-6441: New cascade filter fields
    division?: string;
    companyCountry?: string;
  };
  textInputs: {
    asnNumber: string;
    supplier: string;
    trackingNumber: string;
    poNumber: string;
    poLineNumber: string;
    partNumber: string;
    description: string;
  };
  siteOptions: ASNFilterOption[];
  warehouseOptions: ASNFilterOption[];
  supplierCountryOptions: ASNFilterOption[];
  supplierGroupOptions: ASNFilterOption[];
  /** CR-6449: supplier sites narrowed by Site / Supplier Country / Supplier Group. */
  supplierSiteOptions: ASNFilterOption[];
  commodityGroupOptions: ASNFilterOption[];
  commodityTypeOptions: ASNFilterOption[];
  quickSelectValue: string;
  onFilterChange: (key: string, value: string) => void;
  onQuickSelectChange: (value: string) => void;
  onClearFilters: () => void;
  onSearch: () => void;
  /**
   * CR-6449: render the supplier-view layout from the supplied design. The
   * customer layout is unchanged.
   */
  supplierLayout?: boolean;
  /** Collapsed state. The header stays visible when collapsed. */
  expanded?: boolean;
  onShowFilters?: () => void;
  onHideFilters?: () => void;
  onRefresh?: () => void;
  onExport?: () => void;
  exporting?: boolean;
  /** Number of filters currently applied, shown as a badge in the header. */
  activeFilterCount?: number;
  /** CR-6441: Division options for cascade filtering */
  divisionOptions?: ASNFilterOption[];
  /** CR-6441: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6160: Whether ASN logistics tracking module is enabled - gates courier status filter */
  trackingEnabled?: boolean;
  /** CR-6441 / CR-6160: Courier Delivery Status choices as served by the backend. */
  courierStatusOptions?: ASNFilterOption[];
}

export function ASNFilterSection({
  filters,
  textInputs,
  siteOptions,
  warehouseOptions,
  supplierCountryOptions,
  supplierGroupOptions,
  supplierSiteOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  quickSelectValue,
  onFilterChange,
  onQuickSelectChange,
  onClearFilters,
  onSearch,
  supplierLayout = false,
  expanded = true,
  onShowFilters,
  onHideFilters,
  onRefresh,
  onExport,
  exporting = false,
  activeFilterCount = 0,
  divisionOptions = [],
  siteCountries = [],
  trackingEnabled = false,
  courierStatusOptions,
}: ASNFilterSectionProps) {
  const filterRecord = filters as unknown as Record<string, string>;
  const textRecord = textInputs as unknown as Record<string, string>;

  return (
    <Card>
      {/* Filter header — stays visible when the panel is collapsed. */}
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base text-xl">Filter ASNs</CardTitle>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Button size="sm" variant="outline" onClick={onRefresh}>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Refresh
              </Button>
            )}
            {onExport && (
              <Button size="sm" variant="outline" onClick={onExport} disabled={exporting}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Export
              </Button>
            )}
            <button
              onClick={expanded ? onHideFilters : onShowFilters}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                expanded
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  expanded
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4 p-0">
          {supplierLayout ? (
            <ASNFilterSupplierLayout
              filters={filterRecord}
              textInputs={textRecord}
              quickSelectValue={quickSelectValue}
              onQuickSelectChange={onQuickSelectChange}
              siteOptions={siteOptions}
              warehouseOptions={warehouseOptions}
              supplierCountryOptions={supplierCountryOptions}
              supplierGroupOptions={supplierGroupOptions}
              supplierSiteOptions={supplierSiteOptions}
              onFilterChange={onFilterChange}
              onApply={onSearch}
              trackingEnabled={trackingEnabled}
              courierStatusOptions={courierStatusOptions}
            />
          ) : (
            <ASNFilterSections
              filters={filterRecord}
              textInputs={textRecord}
              siteOptions={siteOptions}
              warehouseOptions={warehouseOptions}
              supplierCountryOptions={supplierCountryOptions}
              supplierGroupOptions={supplierGroupOptions}
              supplierSiteOptions={supplierSiteOptions}
              commodityGroupOptions={commodityGroupOptions}
              commodityTypeOptions={commodityTypeOptions}
              quickSelectValue={quickSelectValue}
              onFilterChange={onFilterChange}
              onQuickSelectChange={onQuickSelectChange}
              onApply={onSearch}
              divisionOptions={divisionOptions}
              siteCountries={siteCountries}
              trackingEnabled={trackingEnabled}
              courierStatusOptions={courierStatusOptions}
            />
          )}
          {/* Filter footer — Clear all left, Apply filters right. */}
          <div className="flex items-center justify-between px-6 pb-4">
            <Button size="sm" variant="outline" onClick={onClearFilters}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Clear all
            </Button>
            <Button size="sm" onClick={onSearch}>
              <Search className="h-3.5 w-3.5 mr-1.5" />
              Apply filters
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
